import { webpushConfig } from 'reactApp/appHelpers/featuresHelpers/features/webPush';
import { getWebPushPromoStatus, resetWebPushPromo } from 'reactApp/modules/promoNew/items/webPushPromoStatus';

import { log } from './shared';
import {
    getSentSubscriptionKey,
    getSentToServerValue,
    getStoredProvider,
    getWebPushAccounts,
    isTokenSentToServer,
} from './webPush.storage';
import { getActiveSwRegistration } from './webPush.sw';

/** Диагностика web-push для консоли (app.webPush.status()): почему приходят/не приходят пуши. */
// eslint-disable-next-line max-lines-per-function
export async function getWebPushStatus() {
    const status: Record<string, unknown> = {
        provider: webpushConfig.provider,
        storedProvider: getStoredProvider(),
        // eslint-disable-next-line compat/compat
        permission: 'Notification' in window ? Notification.permission : 'unsupported',
        sentToServer: getSentToServerValue(),
        promo: getWebPushPromoStatus(),
    };

    if ('serviceWorker' in navigator) {
        try {
            // Health check (read-only): все SW у origin — ловит осиротевшие/дублирующиеся
            // регистрации (например, старый firebase-messaging-sw.js рядом с текущим).
            // eslint-disable-next-line compat/compat
            const registrations = await navigator.serviceWorker.getRegistrations();

            status.registrations = registrations.map((r) => ({
                scope: r.scope,
                active: r.active?.scriptURL,
                activeState: r.active?.state,
                waiting: r.waiting?.scriptURL,
                installing: r.installing?.scriptURL,
            }));
        } catch (error) {
            status.registrationsError = String(error);
        }
    }

    try {
        const reg = await getActiveSwRegistration();

        status.sw = { scope: reg.scope, scriptURL: reg.active?.scriptURL, active: Boolean(reg.active), state: reg.active?.state };

        const sub = await reg.pushManager.getSubscription();

        if (!sub) {
            status.subscription = null;
            return status;
        }

        const json = sub.toJSON();

        status.subscription = {
            endpoint: json.endpoint,
            hasKeys: Boolean(json.keys?.p256dh && json.keys?.auth),
            expirationTime: json.expirationTime,
        };

        const accounts = await getWebPushAccounts();
        const currentKey = await getSentSubscriptionKey(accounts);

        status.currentKey = currentKey;
        status.sentMatchesCurrent = Boolean(currentKey && isTokenSentToServer(currentKey));
    } catch (error) {
        status.error = String(error);
    }

    return status;
}

/**
 * Действия консольного API. Инжектятся из entry, чтобы этот модуль не импортировал
 * оркестрацию (deleteToken/requestPermission/…) и не образовывал цикл.
 * Возвращаемое значение произвольное — это debug-утилиты (напр. unsubscribe отдаёт отчёт).
 */
export interface WebPushConsoleActions {
    resubscribe: () => unknown;
    unsubscribe: () => unknown;
    requestPermission: () => unknown;
}

// Консольные утилиты web-push в window.app (как initConsoleApi в settings.saga).
export function initWebPushConsoleApi(actions: WebPushConsoleActions) {
    window['app'] = window['app'] ?? {};
    Object.assign(window['app'], {
        webPush: {
            /** Диагностика: провайдер, permission, SW, подписка, кэш отправки. */
            status: () => getWebPushStatus(),
            /** Форс-переподписка: сбросить кэш, снять подписку/токен и переподписаться. */
            resubscribe: actions.resubscribe,
            /** Снять подписку без переподписки и сбросить кэш отправки. */
            unsubscribe: actions.unsubscribe,
            /** Запросить разрешение на уведомления вручную. */
            requestPermission: actions.requestPermission,
            /** Сбросить показ промо-тултипа включения уведомлений (нужна перезагрузка страницы). */
            resetPromo: () => {
                resetWebPushPromo();
                log('Web-push promo reset. Reload the page to re-evaluate.');
                return { reset: true, reloadRequired: true };
            },
        },
    });
}
